// The yard listing: search, filters, sort, FLIP-animated reflow, and state kept in the URL.
import { state } from "./core.ts"

const GROUPS = ["family", "job", "status", "paid"] as const
type Group = (typeof GROUPS)[number]
const isGroup = (g: string | undefined): g is Group =>
  (GROUPS as ReadonlyArray<string | undefined>).includes(g)

type Sorter = (a: HTMLElement, b: HTMLElement) => number
const num = (v: string | undefined) => Number(v)
const name = (el: HTMLElement) => el.dataset.name ?? ""
const featured: Sorter = (a, b) =>
  num(b.dataset.featured) - num(a.dataset.featured) || num(a.dataset.order) - num(b.dataset.order)
const SORTERS: Readonly<Record<string, Sorter>> = {
  featured,
  name: (a, b) => name(a).localeCompare(name(b)),
  own: (a, b) => num(a.dataset.own) - num(b.dataset.own) || name(a).localeCompare(name(b)),
  saving: (a, b) => num(b.dataset.rent) - num(b.dataset.own) - (num(a.dataset.rent) - num(a.dataset.own)),
}

export function initListing(): void {
  const wrap = document.getElementById("listing")
  const grid = wrap?.querySelector<HTMLElement>(".apps")
  const q = wrap?.querySelector<HTMLInputElement>("#q")
  const sortSel = wrap?.querySelector<HTMLSelectElement>("#sort")
  const count = wrap?.querySelector<HTMLElement>(".count")
  const empty = wrap?.querySelector<HTMLElement>(".empty")
  if (!wrap || !grid || !q || !sortSel || !count || !empty) return
  const cards = [...grid.children].filter((c): c is HTMLElement => c instanceof HTMLElement)
  const st: { q: string; sort: string } & Record<Group, Set<string>> = {
    q: "",
    sort: "featured",
    family: new Set(),
    job: new Set(),
    status: new Set(),
    paid: new Set(),
  }
  // URL -> state
  const params = new URLSearchParams(location.search)
  st.q = params.get("q") || ""
  st.sort = params.get("sort") || "featured"
  for (const g of GROUPS) {
    for (const v of (params.get(g) || "").split(",").filter(Boolean)) st[g].add(v)
  }
  q.value = st.q
  sortSel.value = st.sort
  const chips = [...wrap.querySelectorAll<HTMLElement>("[data-g][data-v]")]
  const showN = wrap.querySelector<HTMLElement>(".fl-n")
  const badge = wrap.querySelector<HTMLElement>(".fl-badge")
  /**
   * Faceted counts, as marketplaces show them: each option counts the apps you'd see if you also picked
   * it, given the search and every other group's filters. Options that would empty the grid dim.
   */
  const syncChips = () => {
    for (const c of chips) {
      const g = c.dataset.g
      const v = c.dataset.v ?? ""
      if (!isGroup(g)) continue
      const on = st[g].has(v)
      c.setAttribute("aria-pressed", String(on))
      const n = cards.filter((card) => matchExcept(card, g) && value(card, g) === v).length
      const fc = c.querySelector(".fc")
      if (fc) fc.textContent = String(n)
      c.classList.toggle("none", n === 0 && !on)
    }
    const active = GROUPS.reduce((sum, g) => sum + st[g].size, 0) + (st.q ? 1 : 0)
    if (badge) {
      badge.hidden = active === 0
      badge.textContent = String(active)
    }
  }
  const toURL = () => {
    const p = new URLSearchParams()
    if (st.q) p.set("q", st.q)
    for (const g of GROUPS) if (st[g].size) p.set(g, [...st[g]].join(","))
    if (st.sort !== "featured") p.set("sort", st.sort)
    history.replaceState(null, "", location.pathname + (p.toString() ? `?${p}` : ""))
  }
  /** A card's value for a filter group, as its data attributes carry it. */
  const value = (c: HTMLElement, g: Group) => {
    const d = c.dataset
    return (g === "family" ? d.family : g === "job" ? d.jobtag : g === "status" ? d.status : d.paid) ?? ""
  }
  const searchHit = (c: HTMLElement) => {
    if (!st.q) return true
    const d = c.dataset
    const text = `${d.name} ${d.job} ${d.replaces} ${d.familyName}`.toLowerCase()
    return st.q
      .toLowerCase()
      .split(/\s+/)
      .every((w) => text.includes(w))
  }
  /** Matches the search and every active group except `skip` (for faceted counts). */
  const matchExcept = (c: HTMLElement, skip?: Group) =>
    searchHit(c) && GROUPS.every((g) => g === skip || !st[g].size || st[g].has(value(c, g)))
  const match = (c: HTMLElement) => matchExcept(c)
  const apply = (animate = true) => {
    const first = new Map(cards.map((c) => [c, c.hidden ? null : c.getBoundingClientRect()]))
    const shown = cards.filter(match).sort(SORTERS[st.sort] || featured)
    for (const c of cards) c.hidden = !shown.includes(c)
    for (const c of shown) grid.appendChild(c)
    count.textContent = `${shown.length} of ${cards.length} apps`
    if (showN) showN.textContent = String(shown.length)
    empty.classList.toggle("show", !shown.length)
    syncChips()
    toURL()
    if (!animate || state.reduced) return
    for (const c of shown) {
      const a = first.get(c)
      const b = c.getBoundingClientRect()
      if (a) {
        const dx = a.left - b.left
        const dy = a.top - b.top
        if (dx || dy) {
          c.animate([{ transform: `translate(${dx}px,${dy}px)` }, { transform: "none" }], {
            duration: 520,
            easing: "cubic-bezier(.16,1,.3,1)",
          })
        }
      } else {
        c.animate(
          [
            { opacity: 0, transform: "scale(.96)" },
            { opacity: 1, transform: "none" },
          ],
          {
            duration: 420,
            easing: "cubic-bezier(.16,1,.3,1)",
          },
        )
      }
    }
  }
  for (const c of chips) {
    c.addEventListener("click", () => {
      const g = c.dataset.g
      const v = c.dataset.v ?? ""
      if (!isGroup(g)) return
      const s = st[g]
      if (s.has(v)) s.delete(v)
      else s.add(v)
      apply()
    })
  }
  let tq: ReturnType<typeof setTimeout> | undefined
  q.addEventListener("input", () => {
    clearTimeout(tq)
    tq = setTimeout(() => {
      st.q = q.value.trim()
      apply()
    }, 120)
  })
  sortSel.addEventListener("change", () => {
    st.sort = sortSel.value
    apply()
  })
  for (const b of wrap.querySelectorAll("[data-reset]")) {
    b.addEventListener("click", () => {
      for (const g of GROUPS) st[g].clear()
      st.q = ""
      q.value = ""
      st.sort = "featured"
      sortSel.value = "featured"
      apply()
    })
  }
  initDrawer(wrap)
  apply(false)
}

/**
 * On narrow screens the sidebar is a bottom drawer: opened by the Filters button, closed by the scrim,
 * the close button, "Show n apps" or Escape. Focus moves into it and back to the button, and the page
 * behind doesn't scroll while it's open. On wide screens it's a plain sticky sidebar and none of this runs.
 */
function initDrawer(wrap: HTMLElement): void {
  const yard = wrap.querySelector<HTMLElement>(".yard")
  const panel = wrap.querySelector<HTMLElement>("#filters")
  const opener = wrap.querySelector<HTMLButtonElement>("[data-filters-open]")
  const scrim = wrap.querySelector<HTMLElement>(".fl-scrim")
  if (!yard || !panel || !opener || !scrim) return
  const narrow = matchMedia("(max-width: 1024px)")
  const set = (open: boolean) => {
    yard.classList.toggle("open", open)
    scrim.hidden = !open
    opener.setAttribute("aria-expanded", String(open))
    document.documentElement.classList.toggle("fl-lock", open)
    if (open) panel.querySelector<HTMLElement>(".fl-close")?.focus()
    else opener.focus({ preventScroll: true })
  }
  opener.addEventListener("click", () => set(true))
  for (const el of wrap.querySelectorAll("[data-filters-close]")) {
    el.addEventListener("click", () => {
      if (yard.classList.contains("open")) set(false)
    })
  }
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && yard.classList.contains("open")) set(false)
  })
  // Widening the window past the breakpoint turns the drawer back into the sidebar.
  narrow.addEventListener("change", (e) => {
    if (!e.matches && yard.classList.contains("open")) set(false)
  })
}
